/**
 * PaperlessCore API Client — domain facade (Spec 059).
 *
 * Composes the shared fetch core (`lib/api/core.ts`) with one instance per domain module
 * (`lib/api/{documents,upload,tags,reports,sharing,settings,households,integrations}.ts`).
 * Call sites use the domain sub-namespace, e.g. `api.documents.getDocument(id)` (E1).
 */
import { ApiCore } from './api/core';
import { DocumentsApi } from './api/documents';
import { UploadApi } from './api/upload';
import { TagsApi } from './api/tags';
import { ReportsApi } from './api/reports';
import { SharingApi } from './api/sharing';
import { SettingsApi } from './api/settings';
import { HouseholdsApi } from './api/households';
import { IntegrationsApi } from './api/integrations';
import { FollowupsApi } from './api/followups';
import { ChatApi } from './api/chat';

export class ApiClient {
  readonly documents: DocumentsApi;
  readonly upload: UploadApi;
  readonly tags: TagsApi;
  readonly reports: ReportsApi;
  readonly sharing: SharingApi;
  readonly settings: SettingsApi;
  readonly households: HouseholdsApi;
  readonly integrations: IntegrationsApi;
  readonly followups: FollowupsApi;
  readonly chat: ChatApi;

  /** Shared fetch core (baseUrl, auth headers, error handling). @internal — tests only. */
  readonly core: ApiCore;

  constructor(baseUrl?: string) {
    this.core = new ApiCore(baseUrl);
    this.documents = new DocumentsApi(this.core);
    this.upload = new UploadApi(this.core);
    this.tags = new TagsApi(this.core);
    this.reports = new ReportsApi(this.core);
    this.sharing = new SharingApi(this.core);
    this.settings = new SettingsApi(this.core);
    this.households = new HouseholdsApi(this.core);
    this.integrations = new IntegrationsApi(this.core);
    this.followups = new FollowupsApi(this.core);
    this.chat = new ChatApi(this.core);
  }
}

// Re-export all domain types from their new homes (E4) so existing `from './api'` imports
// keep working unchanged.
export type { PaginatedResponse, ProblemDetails } from './api/core';
export type {
  Document,
  Category,
  SubCategory,
  DashboardStats,
  FeedbackResponse,
  BinnedDocument,
  RestoreDocumentsBatchResult,
} from './api/documents';
export type {
  UploadedDocument,
  UploadResponse,
  SkippedFile,
  ChunkedUploadResult,
  UploadBatchStatus,
  UploadBatchDocumentClassification,
} from './api/upload';
export { MAX_FILE_BYTES } from './api/upload';
export type { UserTagCount } from './api/tags';
export type {
  ReportFilterField,
  DataSourceInfo,
  ReportType,
  ReportEntryDto,
  ReportQueryResponse,
  DocumentCatalogEntry,
  RelatedDocumentsResponse,
  CorrelationKeysResponse,
  CorrelationEntitiesResponse,
  DirectoryTreeResponse,
  DirectoryTreeParams,
  ResponsibleNode,
  CategoryNode,
  SubCategoryNode,
  TreeDocument,
  DuplicateGroupsResponse,
  DuplicateGroupDto,
  DuplicateDocumentDto,
} from './api/reports';
export type {
  ShareLinkVisibility,
  CreateShareLinkRequest,
  ShareLinkDto,
  ShareLinkOverviewDto,
  SharedDocumentDto,
  ShareLinkGoneReason,
  ShareLinkGoneResponse,
} from './api/sharing';
export type {
  UserSettings,
  UserProfile,
  UpdateUserSettingsRequest,
  HealthResponse,
  VersionResponse,
  IngestEmailAddressResponse,
  DeleteUserDataResult,
  TimeSeriesPoint,
  HouseholdDocumentCount,
  PersonDocumentCount,
  DocumentStats,
  AiUsageStats,
  PerformanceStats,
  UsageStatsResponse,
} from './api/settings';
export type { HouseholdType, Household, HouseholdMember } from './api/households';
export type {
  NgxIntegrationStatusResponse,
  FileSyncIntegrationStatusResponse,
} from './api/integrations';
export type {
  Followup,
  FollowupDocumentReference,
  FollowupState,
  CreateFollowupRequest,
  UpdateFollowupRequest,
} from './api/followups';

// Export singleton instance
export const api = new ApiClient();
